import React from 'react';
import { toast } from 'sonner';

interface UpgradePromptProps {
    isOpen: boolean;
    onClose: () => void;
    onUpgrade: () => void;
    title: string;
    message: string;
    feature?: string;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
    isOpen,
    onClose,
    onUpgrade,
    title,
    message,
    feature,
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-luna-primary rounded-xl shadow-2xl max-w-md w-full mx-4 border border-luna-text-light/20 dark:border-luna-text-dark/20">
                <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-luna-secondary/10 dark:bg-luna-secondary/20 flex items-center justify-center">
                                <span className="material-symbols-outlined text-luna-secondary text-2xl">
                                    upgrade
                                </span>
                            </div>
                            <h3 className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">
                                {title}
                            </h3>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-luna-text-light/60 dark:text-luna-text-dark/60 hover:text-luna-text-light dark:hover:text-luna-text-dark transition-colors"
                            aria-label="Close"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>

                    <p className="text-luna-text-light/80 dark:text-luna-text-muted-dark mb-6">
                        {message}
                    </p>

                    {feature && (
                        <div className="bg-luna-secondary/5 dark:bg-luna-secondary/10 rounded-lg p-4 mb-6">
                            <div className="flex items-center gap-2 text-sm font-medium text-luna-text-light dark:text-luna-text-dark">
                                <span className="material-symbols-outlined text-luna-secondary">info</span>
                                <span>Feature: {feature}</span>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 text-sm font-medium rounded-lg border border-luna-text-light/20 dark:border-luna-text-dark/20 hover:bg-luna-text-light/5 dark:hover:bg-luna-text-dark/5 transition-colors text-luna-text-light dark:text-luna-text-dark"
                        >
                            Maybe Later
                        </button>
                        <button
                            onClick={() => {
                                onUpgrade();
                                onClose();
                            }}
                            className="flex-1 px-4 py-2.5 text-sm font-bold rounded-lg bg-luna-secondary text-luna-primary hover:opacity-90 transition-opacity shadow-lg shadow-luna-secondary/20"
                        >
                            Upgrade Now
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Show upgrade prompt using toast notification with action button
 * This is a simpler alternative to the modal component
 */
export const showUpgradeToast = (
    message: string,
    onUpgrade: () => void,
    feature?: string
) => {
    toast(message, {
        duration: 5000,
        action: {
            label: 'Upgrade',
            onClick: onUpgrade,
        },
        description: feature ? `Feature: ${feature}` : undefined,
    });
};

